// ============================================================================
// USER MANAGEMENT SCREEN - Digital Approval System
// ============================================================================
// Admin-only screen for resetting passwords of any user belonging to the
// current tenant — including users without DAS application access. Visible
// only when the Administration section is unlocked (das_admin role).
// ============================================================================

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import { useAuth } from 'hooks/api';
import { canSeeModule, hasAction } from 'config/rolePermissions';
import {
  useTenantUsers,
  useAdminResetPassword,
} from 'hooks/api/useTenantUsers';
import type { TenantUser, TenantUserQueryParameters, ID } from 'api/types';
import ResetPasswordModal, {
  type ResetPasswordData,
} from 'components/modals/admin/ResetPasswordModal';
import { SkeletonStyles, TableSkeleton } from 'components/modules/das/DashboardSkeleton';

// ============================================================================
// TYPES
// ============================================================================

interface UserManagementScreenProps {
  className?: string;
}

// ============================================================================
// ROW ACTION MENU (single-action, dropdown for visual consistency)
// ============================================================================

interface RowActionMenuProps {
  rowId: ID;
  userName: string;
  /** When false, the only available action (Reset Password) is hidden. */
  canResetPassword: boolean;
  onResetPassword: (id: ID) => void;
  onMenuStateChange: (rowId: ID, isOpen: boolean) => void;
}

const RowActionMenu: React.FC<RowActionMenuProps> = ({
  rowId,
  canResetPassword,
  onResetPassword,
  onMenuStateChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    onMenuStateChange(rowId, isOpen);
  }, [isOpen, rowId, onMenuStateChange]);

  // No actions available for this user — render a placeholder dash so the
  // column still aligns visually with rows that do have actions.
  if (!canResetPassword) {
    return (
      <span style={{ color: 'var(--text-muted, #9ca3af)', fontSize: '14px' }}>—</span>
    );
  }

  return (
    <div className="action-menu-container" ref={menuRef}>
      <button
        className={`action-menu-trigger ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Row actions"
      >
        <IconSystem.moreVertical size={16} />
      </button>

      {isOpen && (
        <div className="action-menu-dropdown">
          <button
            className="action-menu-item"
            onClick={() => {
              onResetPassword(rowId);
              setIsOpen(false);
            }}
          >
            <IconSystem.lock size={18} />
            <span>Reset Password</span>
          </button>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const UserManagementScreen: React.FC<UserManagementScreenProps> = ({ className = '' }) => {
  const { user: authUser } = useAuth();

  // ---------------------------------------------------------------------------
  // ACCESS GUARD (defense-in-depth — server endpoint is the ultimate authority)
  // ---------------------------------------------------------------------------
  // Configuration-driven: access is gated by the merged role-permissions
  // fetched at login (see useAuth.ts → /admin/role-permissions/by-roles).
  // Any role granted the `user-management` module via the Role Permissions
  // admin UI can open this screen — there is no hardcoded role allow-list.
  // das_system passes automatically because useAuth.ts overrides its merged
  // permissions with the full ALL_MODULES catalog.
  const moduleId = 'user-management';
  const hasAccess = useMemo(
    () => canSeeModule(moduleId, authUser?.rolePermissions),
    [authUser]
  );
  const canResetPasswordAction = useMemo(
    () => hasAction(moduleId, 'reset-password', authUser?.rolePermissions),
    [authUser]
  );

  // ---------------------------------------------------------------------------
  // STATE
  // ---------------------------------------------------------------------------
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [activeMenuRowId, setActiveMenuRowId] = useState<ID | null>(null);

  const [queryParams, setQueryParams] = useState<TenantUserQueryParameters>({
    page: 1,
    pageSize: 10,
    searchTerm: '',
    isActive: undefined,
  });

  const [resetTarget, setResetTarget] = useState<TenantUser | null>(null);
  const [notification, setNotification] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  // ---------------------------------------------------------------------------
  // HOOKS
  // ---------------------------------------------------------------------------
  const { items, pagination, loading, error, refetch } = useTenantUsers(
    hasAccess ? queryParams : undefined
  );
  const { resetPassword, loading: resetting } = useAdminResetPassword();

  // ---------------------------------------------------------------------------
  // HANDLERS
  // ---------------------------------------------------------------------------
  const handleMenuStateChange = useCallback((rowId: ID, isOpen: boolean) => {
    setActiveMenuRowId(isOpen ? rowId : null);
  }, []);

  // Debounced search (server-side)
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSearch = useCallback((value: string) => {
    setSearchQuery(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setQueryParams((prev) => ({ ...prev, searchTerm: value, page: 1 }));
    }, 400);
  }, []);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  const handleApplyFilters = useCallback(() => {
    setQueryParams((prev) => ({
      ...prev,
      isActive: statusFilter === 'all' ? undefined : statusFilter === 'active',
      page: 1,
    }));
    setFilterOpen(false);
  }, [statusFilter]);

  const handleClearFilters = useCallback(() => {
    setStatusFilter('all');
    setQueryParams((prev) => ({ ...prev, isActive: undefined, page: 1 }));
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setQueryParams((prev) => ({ ...prev, page }));
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setQueryParams((prev) => ({ ...prev, pageSize: newPageSize, page: 1 }));
  }, []);

  const showNotification = useCallback((type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    if (type === 'success') {
      setTimeout(() => setNotification(null), 5000);
    }
  }, []);

  const handleOpenResetModal = useCallback(
    (id: ID) => {
      const target = items.find((u) => u.id === id);
      if (target) setResetTarget(target);
    },
    [items]
  );

  const handleCloseResetModal = useCallback(() => {
    setResetTarget(null);
  }, []);

  const handleSubmitReset = useCallback(
    async (data: ResetPasswordData) => {
      if (!resetTarget) return;
      try {
        await resetPassword(resetTarget.id, data, {
          onSuccess: () => {
            showNotification(
              'success',
              `Password reset successfully for ${resetTarget.displayName || resetTarget.username}.`
            );
            setResetTarget(null);
          },
          onError: (err) => {
            // Re-throw so the modal can render its own inline error banner
            throw err;
          },
        });
      } catch (err) {
        // Surface to the modal's inline error
        throw err;
      }
    },
    [resetTarget, resetPassword, showNotification]
  );

  // ---------------------------------------------------------------------------
  // ACTION BUTTONS
  // ---------------------------------------------------------------------------
  const actionButtons: ActionButton[] = [
    {
      id: 'refresh',
      icon: <IconSystem.refresh size={18} />,
      label: 'Refresh',
      onClick: () => refetch(),
      tooltip: 'Refresh',
      variant: 'icon',
    },
  ];

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------
  if (!hasAccess) {
    return (
      <div className={`module-master-container ${className}`}>
        <ModuleHeader
          visible={true}
          title="User Management"
          description="Reset passwords for users belonging to the current tenant"
        />
        <div
          style={{
            margin: '2rem 1.25rem',
            padding: '1.5rem',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            color: '#991b1b',
            fontSize: '0.95rem',
          }}
        >
          <strong>Access Denied.</strong> Your role does not include the User Management module. Contact your administrator to grant access via the Role Permissions screen.
        </div>
      </div>
    );
  }

  return (
    <div className={`module-master-container ${className}`}>
      <SkeletonStyles />
      <ModuleHeader
        visible={true}
        title="User Management"
        description="Reset passwords for any user belonging to the current tenant"
        searchConfig={{
          value: searchQuery,
          onChange: handleSearch,
          placeholder: 'Search users...',
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen,
        }}
        actions={actionButtons}
      />

      {/* Centered toast — rendered via portal to escape header/content layout */}
      {notification && createPortal(
        <div
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10080,
            minWidth: '280px',
            maxWidth: '480px',
            padding: '14px 20px',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '14px',
            backgroundColor: '#2563eb',
            color: '#ffffff',
            boxShadow: '0 12px 24px -8px rgba(37, 99, 235, 0.5), 0 4px 8px rgba(0, 0, 0, 0.15)',
            fontSize: '0.9375rem',
            fontWeight: 500,
            textAlign: 'center',
          }}
          role="status"
          aria-live="polite"
        >
          <span style={{ flex: 1 }}>{notification.message}</span>
          <button
            style={{
              background: 'rgba(255, 255, 255, 0.18)',
              border: 'none',
              borderRadius: '4px',
              fontSize: '18px',
              lineHeight: 1,
              cursor: 'pointer',
              padding: '2px 8px',
              color: '#ffffff',
              flexShrink: 0,
            }}
            onClick={() => setNotification(null)}
            aria-label="Dismiss"
          >
            &times;
          </button>
        </div>,
        document.body
      )}

      {/* Error banner */}
      {error && (
        <div
          style={{
            backgroundColor: '#fef2f2',
            color: '#991b1b',
            padding: '16px',
            borderRadius: '6px',
            margin: '0 1.25rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          Error loading users: {error.message}
          <button className="btn btn-secondary" onClick={() => refetch()}>
            Retry
          </button>
        </div>
      )}

      <div className="module-content" style={{ marginTop: '12px' }}>
        <div className="data-grid-container">
          {/* List view */}
          <div className="data-table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Display Name</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'center' }}>Primary Tenant</th>
                  <th className="action-column" style={{ width: 80 }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <TableSkeleton rowCount={queryParams.pageSize ?? 10} columnCount={6} />
                ) : items.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}
                    >
                      No users found
                    </td>
                  </tr>
                ) : (
                  items.map((u) => (
                    <tr
                      key={u.id as string}
                      className={activeMenuRowId === u.id ? 'has-active-menu' : ''}
                    >
                      <td>
                        <code
                          style={{
                            backgroundColor: '#f3f4f6',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontFamily: 'monospace',
                          }}
                        >
                          {u.username}
                        </code>
                      </td>
                      <td>{u.displayName || `${u.firstName} ${u.lastName}`.trim() || '—'}</td>
                      <td>{u.email || '—'}</td>
                      <td>
                        <span className={`badge badge-${u.isActive ? 'active' : 'inactive'}`}>
                          {u.statusName || (u.isActive ? 'Active' : 'Inactive')}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {u.isPrimaryTenant === undefined ? (
                          <span style={{ color: 'var(--text-muted, #9ca3af)', fontSize: '12px' }}>—</span>
                        ) : (
                          <span
                            style={{
                              display: 'inline-block',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '11px',
                              fontWeight: 600,
                              background: u.isPrimaryTenant ? '#dcfce7' : '#f3f4f6',
                              color: u.isPrimaryTenant ? '#166534' : '#6b7280',
                              border: `1px solid ${u.isPrimaryTenant ? '#86efac' : '#d1d5db'}`,
                            }}
                          >
                            {u.isPrimaryTenant ? 'Yes' : 'No'}
                          </span>
                        )}
                      </td>
                      <td className="action-column">
                        <RowActionMenu
                          rowId={u.id}
                          userName={u.displayName || u.username}
                          // Two gates:
                          //   1. `reset-password` action must be granted to
                          //      the current user's role via Role Permissions
                          //      configuration (canResetPasswordAction).
                          //   2. The user being managed must be a primary
                          //      tenant member (isPrimaryTenant). Missing
                          //      isPrimaryTenant treated as true (API back-
                          //      compat). Only explicit `false` hides.
                          canResetPassword={canResetPasswordAction && u.isPrimaryTenant !== false}
                          onResetPassword={handleOpenResetModal}
                          onMenuStateChange={handleMenuStateChange}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.totalItems > 0 && (
            <div className="pagination-footer">
              <div className="pagination-info">
                <span>
                  Showing{' '}
                  {pagination.totalItems === 0
                    ? 0
                    : (pagination.currentPage - 1) * pagination.pageSize + 1}
                  -{Math.min(pagination.currentPage * pagination.pageSize, pagination.totalItems)} of{' '}
                  {pagination.totalItems} users
                </span>
                <select
                  value={queryParams.pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  style={{
                    marginLeft: '1rem',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    border: '1px solid var(--border-light)',
                    background: 'var(--bg-primary)',
                    color: 'var(--text-primary)',
                    cursor: 'pointer',
                  }}
                >
                  <option value={10}>10 per page</option>
                  <option value={20}>20 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
              </div>
              <div className="pagination-controls">
                <button
                  className="page-btn"
                  disabled={!pagination.hasPreviousPage}
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                >
                  <IconSystem.chevronLeft size={16} />
                </button>
                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                  .filter(
                    (page) =>
                      page === 1 ||
                      page === pagination.totalPages ||
                      Math.abs(page - pagination.currentPage) <= 1
                  )
                  .map((page, index, array) => {
                    const prevPage = array[index - 1];
                    const showEllipsis = prevPage && page - prevPage > 1;
                    return (
                      <React.Fragment key={page}>
                        {showEllipsis && (
                          <span style={{ padding: '0.5rem', color: 'var(--text-muted)' }}>…</span>
                        )}
                        <button
                          className={`page-btn ${pagination.currentPage === page ? 'active' : ''}`}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </button>
                      </React.Fragment>
                    );
                  })}
                <button
                  className="page-btn"
                  disabled={!pagination.hasNextPage}
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                >
                  <IconSystem.chevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Filter panel (sibling) */}
        {filterOpen && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              bottom: 0,
              width: '320px',
              background: 'var(--bg-primary, #fff)',
              borderLeft: '1px solid var(--border-color, #e5e7eb)',
              boxShadow: '-4px 0 12px rgba(0, 0, 0, 0.08)',
              zIndex: 100,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '1rem 1.25rem',
                borderBottom: '1px solid var(--border-color, #e5e7eb)',
              }}
            >
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Filters</h3>
              <button
                onClick={() => setFilterOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  color: 'var(--text-muted, #6b7280)',
                }}
                aria-label="Close filters"
              >
                <IconSystem.x size={18} />
              </button>
            </div>

            <div style={{ flex: 1, padding: '1.25rem', overflowY: 'auto' }}>
              <div style={{ marginBottom: '1.25rem' }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    marginBottom: '0.5rem',
                    color: 'var(--text-secondary, #374151)',
                  }}
                >
                  Status
                </label>
                {(['all', 'active', 'inactive'] as const).map((opt) => (
                  <label
                    key={opt}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.375rem 0',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                    }}
                  >
                    <input
                      type="radio"
                      name="status-filter"
                      checked={statusFilter === opt}
                      onChange={() => setStatusFilter(opt)}
                    />
                    {opt === 'all' ? 'All' : opt === 'active' ? 'Active' : 'Inactive'}
                  </label>
                ))}
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                gap: '0.5rem',
                padding: '1rem 1.25rem',
                borderTop: '1px solid var(--border-color, #e5e7eb)',
              }}
            >
              <button
                className="btn btn-secondary"
                onClick={handleClearFilters}
                style={{ flex: 1 }}
              >
                Clear
              </button>
              <button
                className="btn btn-primary"
                onClick={handleApplyFilters}
                style={{ flex: 1 }}
              >
                Apply
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Reset Password Modal */}
      <ResetPasswordModal
        isOpen={!!resetTarget}
        onClose={handleCloseResetModal}
        onSubmit={handleSubmitReset}
        userName={
          resetTarget
            ? resetTarget.displayName ||
              `${resetTarget.firstName} ${resetTarget.lastName}`.trim() ||
              resetTarget.username
            : ''
        }
        userSubLabel={resetTarget?.email || resetTarget?.username}
        isLoading={resetting}
      />
    </div>
  );
};

export default UserManagementScreen;
